import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Sun, Moon } from "react-bootstrap-icons";
import styled from "styled-components";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    ReferenceLine
} from "recharts";

import { getAnalyticsWidget } from "../../utils/Requests";
import { getDefaultDateRange, getWidgetTheme } from "../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, WidgetTheme } from "../../types/analytics";
import AnalyticsFiltersComponent from "../../components/analytics/AnalyticsFilters";
import Logger from "../../utils/Logger";

type KpiDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type KpiMetricCard = {
    key: string;
    label: string;
    value: number | null;
    changePercent: number | null;
    valueFormat?: "number" | "percent" | "decimal";
};

type SessionTrendPoint = {
    date: string;
    sessionCount: number;
};

type TopPlatformPoint = {
    devicePlatform: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type TopChannelPoint = {
    thingName: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type KpiDashboardState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    isDarkTheme: boolean;
    trafficMetrics: KpiMetricCard[];
    userEngagementMetrics: KpiMetricCard[];
    sessionTrendData: SessionTrendPoint[];
    topPlatformsData: TopPlatformPoint[];
    topChannelsData: TopChannelPoint[];
};

const SingleRowMetricGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
`;

const MetricCard = styled.div<{ theme: WidgetTheme }>`
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    padding: 16px;
    background: ${props => props.theme.chartBg};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
`;

const MetricLabel = styled.div<{ theme: WidgetTheme }>`
    font-size: 0.9rem;
    color: ${props => props.theme.text};
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div<{ theme: WidgetTheme }>`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${props => props.theme.text};
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? "#28a745" : props.$negative ? "#dc3545" : "#6c757d"};
`;

const ChartContainer = styled.div<{ theme: WidgetTheme }>`
    padding: 16px;
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    background: ${props => props.theme.chartBg};
`;

const SectionContainer = styled.div`
    margin-top: 24px;
`;

const SectionHeader = styled.h5<{ theme: WidgetTheme }>`
    margin-bottom: 12px;
    color: ${props => props.theme.text};
`;

const TopKpiGroups = styled.div`
    display: flex;
    gap: 24px;
    align-items: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
    margin-top: 24px;
    padding-bottom: 4px;
`;

const KpiGroup = styled.div`
    min-width: fit-content;
`;

const SubChartBlock = styled.div<{ theme: WidgetTheme }>`
    margin-top: 18px;

    h6 {
        color: ${props => props.theme.text};
        margin-bottom: 8px;
    }
`;

const BackToBackCharts = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
`;

class KpiDashboard extends React.Component<KpiDashboardProps, KpiDashboardState> {
    constructor(props: KpiDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };

        this.state = {
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            filterKey: 0,
            isDarkTheme: true,
            trafficMetrics: [],
            userEngagementMetrics: [],
            sessionTrendData: [],
            topPlatformsData: [],
            topChannelsData: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private formatMetricValue = (metric: KpiMetricCard): string => {
        if (metric.value === null || metric.value === undefined) {
            return "-";
        }

        if (metric.valueFormat === "percent") {
            return `${metric.value.toFixed(2)}%`;
        }

        if (metric.valueFormat === "decimal") {
            return metric.value.toFixed(2);
        }

        return metric.value.toLocaleString();
    };

    private formatChange = (changePercent: number | null): string => {
        if (changePercent === null || changePercent === undefined) {
            return "No change data";
        }

        const sign = changePercent > 0 ? "+" : "";
        return `${sign}${changePercent.toFixed(2)}%`;
    };

    private mapResponseToMetrics = (
        responseRow: Record<string, number>,
        preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" }>
    ): KpiMetricCard[] => {
        const preferred = preferredMetricMap
            .filter(metric => Object.prototype.hasOwnProperty.call(responseRow, metric.key))
            .map(metric => ({
                key: metric.key,
                label: metric.label,
                value: responseRow[metric.key] ?? null,
                changePercent: responseRow[metric.changeKey] ?? null,
                valueFormat: metric.valueFormat
            }));

        if (preferred.length > 0) {
            return preferred;
        }

        // Fallback: derive metric cards from *_percent_change pairs.
        return Object.keys(responseRow)
            .filter(key => !key.endsWith("_percent_change"))
            .map(key => {
                const changeKey = `${key}_percent_change`;
                return {
                    key,
                    label: this.toTitleCase(key),
                    value: responseRow[key] ?? null,
                    changePercent: responseRow[changeKey] ?? null,
                    valueFormat: key.endsWith("_percent") ? "percent" : "number"
                };
            });
    };

    private mapTrafficMetrics = (responseRow: Record<string, number>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" }> = [
            { key: "session_count", label: "Sessions", changeKey: "session_percent_change" },
            { key: "device_count", label: "Devices", changeKey: "device_percent_change" },
            { key: "play_count", label: "Plays", changeKey: "plays_percent_change" },
            {
                key: "gdpr_status_device_percent",
                label: "GDPR Status Device %",
                changeKey: "gdpr_status_device_percent_change",
                valueFormat: "percent"
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap);
    };

    private mapUserEngagementMetrics = (responseRow: Record<string, number>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" }> = [
            {
                key: "sessions_per_user",
                label: "Sessions per User",
                changeKey: "sessions_per_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "plays_per_user",
                label: "Plays per User",
                changeKey: "plays_per_user_percent_change",
                valueFormat: "decimal"
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap);
    };

    private formatTrendDate = (value: string): string => {
        try {
            const date = new Date(`${value}T00:00:00Z`);
            return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        } catch {
            return value;
        }
    };

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;
        
        try {
            this.setState({ isLoading: true, error: null });

            const [trafficResponse, engagementResponse, sessionTrendResponse, topPlatformsResponse, topThingsResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_traffic",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_user_engagement",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_session_trend",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_top_platforms",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_top_things",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                )
            ]);

            const trafficData = (trafficResponse as any)?.result;
            const trafficResultRow = Array.isArray(trafficData) && trafficData.length > 0 ? trafficData[0] : null;

            const engagementData = (engagementResponse as any)?.result;
            const engagementResultRow = Array.isArray(engagementData) && engagementData.length > 0 ? engagementData[0] : null;

            const trafficMetrics = trafficResultRow && typeof trafficResultRow === "object"
                ? this.mapTrafficMetrics(trafficResultRow as Record<string, number>)
                : [];

            const userEngagementMetrics = engagementResultRow && typeof engagementResultRow === "object"
                ? this.mapUserEngagementMetrics(engagementResultRow as Record<string, number>)
                : [];

            const trendDataRaw = (sessionTrendResponse as any)?.result;
            const sessionTrendData = (Array.isArray(trendDataRaw) ? trendDataRaw : [])
                .filter((row: Record<string, any>) => row?.date && typeof row?.session_count === "number")
                .map((row: Record<string, any>) => ({
                    date: row.date,
                    sessionCount: row.session_count
                }))
                .sort((a: SessionTrendPoint, b: SessionTrendPoint) => a.date.localeCompare(b.date));

            const topPlatformsRaw = (topPlatformsResponse as any)?.result;
            const topPlatformsData = (Array.isArray(topPlatformsRaw) ? topPlatformsRaw : [])
                .filter((row: Record<string, any>) => row?.device_platform)
                .map((row: Record<string, any>) => ({
                    devicePlatform: row.device_platform,
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopPlatformPoint, b: TopPlatformPoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            const topThingsRaw = (topThingsResponse as any)?.result;
            const topChannelsData = (Array.isArray(topThingsRaw) ? topThingsRaw : [])
                .map((row: Record<string, any>) => ({
                    thingName: row.thing_name || "Unknown",
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopChannelPoint, b: TopChannelPoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            this.setState({
                trafficMetrics,
                userEngagementMetrics,
                sessionTrendData,
                topPlatformsData,
                topChannelsData
            });
        } catch (err) {
            Logger.error("Error fetching KPI dashboard data", err);
            this.setState({ error: "Failed to fetch KPI dashboard data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private renderMetricCards = (metrics: KpiMetricCard[], theme: WidgetTheme): ReactNode => {
        if (metrics.length === 0) {
            return <Alert variant="info">No KPI data available for the selected date range.</Alert>;
        }

        return (
            <SingleRowMetricGrid>
                {metrics.map(metric => {
                    const positive = (metric.changePercent ?? 0) > 0;
                    const negative = (metric.changePercent ?? 0) < 0;

                    return (
                        <MetricCard key={metric.key} theme={theme} style={{ minWidth: "220px" }}>
                            <MetricLabel theme={theme}>{metric.label}</MetricLabel>
                            <MetricValue theme={theme}>{this.formatMetricValue(metric)}</MetricValue>
                            <MetricChange $positive={positive} $negative={negative}>
                                {this.formatChange(metric.changePercent)}
                            </MetricChange>
                        </MetricCard>
                    );
                })}
            </SingleRowMetricGrid>
        );
    };

    private renderSessionTrendChart = (theme: WidgetTheme): ReactNode => {
        const { sessionTrendData } = this.state;

        if (sessionTrendData.length === 0) {
            return <Alert variant="info" style={{ marginTop: "20px" }}>No session trend data available for the selected date range.</Alert>;
        }

        return (
            <SectionContainer>
                <SectionHeader theme={theme}>Session Trend</SectionHeader>
                <ChartContainer theme={theme}>
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={sessionTrendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tickFormatter={this.formatTrendDate} />
                            <YAxis tickFormatter={(value: number) => value.toLocaleString()} />
                            <Tooltip
                                labelFormatter={(value: string) => this.formatTrendDate(value)}
                                formatter={(value: number) => [value.toLocaleString(), "Sessions"]}
                                contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                            />
                            <Line
                                type="monotone"
                                dataKey="sessionCount"
                                stroke="#007bff"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                name="Sessions"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </SectionContainer>
        );
    };

    private renderTopPlatformsSection = (theme: WidgetTheme): ReactNode => {
        const { topPlatformsData } = this.state;

        if (topPlatformsData.length === 0) {
            return (
                <SectionContainer>
                    <SectionHeader theme={theme}>Top Platforms</SectionHeader>
                    <Alert variant="info">No top platform data available for the selected date range.</Alert>
                </SectionContainer>
            );
        }

        return (
            <SectionContainer>
                <SectionHeader theme={theme}>Top Platforms</SectionHeader>
                <ChartContainer theme={theme}>
                    <SubChartBlock theme={theme}>
                        <BackToBackCharts>
                            <div>
                                <h6>Session Count</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topPlatformsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => value.toLocaleString()}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="devicePlatform"
                                            width={95}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                        />
                                        <Tooltip
                                            labelFormatter={(value: string) => value}
                                            formatter={(value: number) => [value.toLocaleString(), "Sessions"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionCount" fill="#007bff" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h6>Session % Change</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topPlatformsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            domain={[(dataMin: number) => Math.min(dataMin, 0), "auto"]}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => `${value}%`}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="devicePlatform"
                                            width={95}
                                            tick={false}
                                            axisLine={{ stroke: theme.border }}
                                        />
                                        <ReferenceLine x={0} stroke={theme.text} strokeOpacity={0.5} />
                                        <Tooltip
                                            labelFormatter={(value: string) => value}
                                            formatter={(value: number) => [`${value.toFixed(2)}%`, "Session % Change"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionPercentChange">
                                            {topPlatformsData.map((entry: TopPlatformPoint) => (
                                                <Cell
                                                    key={`pct-${entry.devicePlatform}`}
                                                    fill={entry.sessionPercentChange >= 0 ? "#28a745" : "#dc3545"}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </BackToBackCharts>
                    </SubChartBlock>
                </ChartContainer>
            </SectionContainer>
        );
    };

    private renderTopChannelsSection = (theme: WidgetTheme): ReactNode => {
        const { topChannelsData } = this.state;

        if (topChannelsData.length === 0) {
            return (
                <SectionContainer>
                    <SectionHeader theme={theme}>Top Channels</SectionHeader>
                    <Alert variant="info">No top channel data available for the selected date range.</Alert>
                </SectionContainer>
            );
        }

        return (
            <SectionContainer>
                <SectionHeader theme={theme}>Top Channels</SectionHeader>
                <ChartContainer theme={theme}>
                    <SubChartBlock theme={theme}>
                        <BackToBackCharts>
                            <div>
                                <h6>Session Count</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topChannelsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => value.toLocaleString()}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="thingName"
                                            width={95}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                        />
                                        <Tooltip
                                            labelFormatter={(value: string) => value}
                                            formatter={(value: number) => [value.toLocaleString(), "Sessions"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionCount" fill="#007bff" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h6>Session % Change</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topChannelsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            domain={[(dataMin: number) => Math.min(dataMin, 0), "auto"]}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => `${value}%`}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="thingName"
                                            width={95}
                                            tick={false}
                                            axisLine={{ stroke: theme.border }}
                                        />
                                        <ReferenceLine x={0} stroke={theme.text} strokeOpacity={0.5} />
                                        <Tooltip
                                            labelFormatter={(value: string) => value}
                                            formatter={(value: number) => [`${value.toFixed(2)}%`, "Session % Change"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionPercentChange">
                                            {topChannelsData.map((entry: TopChannelPoint, index: number) => (
                                                <Cell
                                                    key={`channel-pct-${entry.thingName}-${index}`}
                                                    fill={entry.sessionPercentChange >= 0 ? "#28a745" : "#dc3545"}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </BackToBackCharts>
                    </SubChartBlock>
                </ChartContainer>
            </SectionContainer>
        );
    };

    public render(): ReactNode {
        const { isLoading, error, filters, filterOptions, isDarkTheme, trafficMetrics, userEngagementMetrics } = this.state;
        const theme = getWidgetTheme(isDarkTheme);

        return (
            <div>
                <Container className="mw-100" key="kpi-dashboard" style={{ position: "relative" }}>
                    <div style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px" }}>
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>KPI Dashboard</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                    />

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "40px" }}>
                                <Spinner animation="border" variant="primary" />
                                <p style={{ marginTop: "10px", color: theme.text }}>Loading KPI dashboard...</p>
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <TopKpiGroups>
                                <KpiGroup>
                                    <SectionHeader theme={theme}>Traffic</SectionHeader>
                                    {this.renderMetricCards(trafficMetrics, theme)}
                                </KpiGroup>
                                <KpiGroup>
                                    <SectionHeader theme={theme}>User Engagement</SectionHeader>
                                    {this.renderMetricCards(userEngagementMetrics, theme)}
                                </KpiGroup>
                            </TopKpiGroups>
                            <Row>
                                <Col>
                                    {this.renderSessionTrendChart(theme)}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    {this.renderTopPlatformsSection(theme)}
                                </Col>
                                <Col md={6}>
                                    {this.renderTopChannelsSection(theme)}
                                </Col>
                            </Row>
                        </>
                    )}
                </Container>
            </div>
        );
    }
}

export default KpiDashboard;
